/*
  AppointmentPathwayMapCard — Settings card that maps Acuity appointment
  types → default assessment pathways so the "New assessment" dialog
  can pre-tick the right modules automatically.

  Sits under `SectionCategoriesCard` in Settings → Clinical.

  Actions:
    - Auto-seed from Acuity /appointment-types (matches by name).
    - Add / edit a mapping row (pick appointment type + pathway).
    - Delete a mapping row.
    - Dismiss the "Review mapping" nudge banner once reviewed.
*/
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Route, Plus, Trash2, RefreshCw, X, Sparkles, Wand2 } from "lucide-react";

export default function AppointmentPathwayMapCard() {
  const [pathways, setPathways] = useState([]);
  const [rows, setRows] = useState([]);
  const [types, setTypes] = useState([]);      // Acuity appointment types
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newTypeId, setNewTypeId] = useState("");
  const [newPathway, setNewPathway] = useState("");

  async function loadAll() {
    setLoading(true);
    try {
      const [pw, mp, st, at] = await Promise.all([
        api.get("/settings/appointment-pathway-map/pathways"),
        api.get("/settings/appointment-pathway-map"),
        api.get("/settings/appointment-pathway-map/settings"),
        api.get("/appointment-types").catch(() => ({ data: [] })),
      ]);
      setPathways(pw.data?.pathways || []);
      setRows(mp.data?.rows || []);
      setSettings(st.data || {});
      setTypes(Array.isArray(at.data) ? at.data : []);
    } catch (e) {
      toast.error("Could not load pathway mapping", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally { setLoading(false); }
  }

  useEffect(() => { loadAll(); }, []);

  const typeById = useMemo(() => {
    const m = new Map();
    (types || []).forEach((t) => m.set(Number(t.id), t));
    return m;
  }, [types]);

  // Types not yet mapped, for the "Add mapping" dropdown.
  const mappedIds = useMemo(
    () => new Set(rows.map((r) => Number(r.appointment_type_id)).filter(Number.isFinite)),
    [rows],
  );
  const unmappedTypes = useMemo(
    () => (types || []).filter((t) => !mappedIds.has(Number(t.id))),
    [types, mappedIds],
  );

  async function runAutoSeed(force = false) {
    setBusy(true);
    try {
      const r = await api.post("/settings/appointment-pathway-map/auto-seed", { force });
      const seeded = r.data?.seeded || [];
      const skipped = r.data?.skipped || [];
      const existing = r.data?.existing || [];
      if (r.data?.skipped_run) {
        toast.info("Auto-seed already ran once", {
          description: "Use ‘Re-seed’ to try again — your manual edits are preserved.",
        });
      } else {
        toast.success(`Auto-seeded ${seeded.length} appointment types`, {
          description: `${skipped.length} had no matching rule · ${existing.length} already mapped.`,
        });
      }
      await loadAll();
    } catch (e) {
      toast.error("Auto-seed failed", { description: e?.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  }

  async function upsertMapping(payload) {
    setBusy(true);
    try {
      await api.post("/settings/appointment-pathway-map", payload);
      toast.success("Mapping saved");
      setAdding(false); setNewTypeId(""); setNewPathway("");
      await loadAll();
    } catch (e) {
      toast.error("Could not save mapping", { description: e?.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  }

  async function deleteRow(id) {
    if (!window.confirm("Remove this mapping?")) return;
    setBusy(true);
    try {
      await api.delete(`/settings/appointment-pathway-map/${id}`);
      toast.success("Mapping removed");
      await loadAll();
    } catch (e) {
      toast.error("Could not delete", { description: e?.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  }

  async function dismissNudge() {
    try {
      await api.post("/settings/appointment-pathway-map/dismiss-nudge");
      setSettings((s) => ({ ...s, review_nudge_dismissed: true }));
    } catch (e) { /* ignore */ }
  }

  const showNudge = settings.auto_seeded && !settings.review_nudge_dismissed;
  const autoSeededRows = rows.filter((r) => r.auto_seeded);

  return (
    <Card
      className="p-4 rounded-lg border border-zinc-200 shadow-none"
      data-testid="appointment-pathway-map-card"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold flex items-center gap-1.5">
            <Route className="h-3.5 w-3.5" /> Assessment templates
          </div>
          <div className="text-sm text-zinc-600 mt-0.5">
            Map each Acuity appointment type to the default assessment pathway. When you
            start a new assessment for a client, the correct modules pre-tick automatically.
          </div>
        </div>
        <div className="flex gap-2">
          {!settings.auto_seeded && (
            <Button
              onClick={() => runAutoSeed(false)}
              size="sm"
              disabled={busy}
              className="bg-zinc-950 hover:bg-zinc-800 text-white"
              data-testid="apm-auto-seed"
            >
              <Sparkles className="h-4 w-4 mr-1.5" /> Auto-seed from Acuity
            </Button>
          )}
          {settings.auto_seeded && (
            <Button
              onClick={() => runAutoSeed(true)}
              variant="outline"
              size="sm"
              disabled={busy}
              data-testid="apm-reseed"
            >
              <RefreshCw className="h-4 w-4 mr-1.5" /> Re-seed
            </Button>
          )}
        </div>
      </div>

      {showNudge && (
        <div
          className="mt-3 p-3 rounded border border-amber-200 bg-amber-50 text-sm text-amber-900 flex items-start justify-between gap-3"
          data-testid="apm-review-nudge"
        >
          <div>
            <b>Review your auto-seeded mappings.</b> We matched{" "}
            {autoSeededRows.length} appointment type
            {autoSeededRows.length === 1 ? "" : "s"} to pathways by name.
            Please confirm each one is correct — clinicians will get these
            modules pre-ticked whenever they start a new assessment.
          </div>
          <button
            type="button"
            onClick={dismissNudge}
            className="text-amber-700 hover:text-amber-900"
            data-testid="apm-dismiss-nudge"
            aria-label="Dismiss review nudge"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="mt-3 space-y-2">
        {loading && <div className="text-sm text-zinc-500">Loading…</div>}
        {!loading && rows.length === 0 && (
          <div className="text-sm text-zinc-500 p-4 border border-dashed border-zinc-200 rounded">
            No mappings yet. Click <b>Auto-seed from Acuity</b> to fill in the obvious ones
            (Onero, GLA:D, Reformer, EP, Parkinson's, Cancer rehab), or add rows manually.
          </div>
        )}
        {!loading && rows.map((row) => (
          <MappingRow
            key={row.id}
            row={row}
            pathways={pathways}
            typeName={typeById.get(Number(row.appointment_type_id))?.name || row.appointment_type_name || "(name unknown)"}
            onChange={(pathway_key) => upsertMapping({
              appointment_type_id: row.appointment_type_id,
              appointment_type_name: row.appointment_type_name,
              pathway_key,
            })}
            onDelete={() => deleteRow(row.id)}
            disabled={busy}
          />
        ))}
      </div>

      {adding ? (
        <div
          className="mt-3 p-3 border border-zinc-200 rounded flex flex-wrap items-end gap-3"
          data-testid="apm-add-row"
        >
          <div className="flex-1 min-w-[220px]">
            <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold mb-1">
              Appointment type
            </div>
            <Select value={newTypeId} onValueChange={setNewTypeId}>
              <SelectTrigger data-testid="apm-add-type">
                <SelectValue placeholder="Pick an Acuity appointment type" />
              </SelectTrigger>
              <SelectContent>
                {unmappedTypes.length === 0 && (
                  <div className="p-2 text-sm text-zinc-500">All types are mapped.</div>
                )}
                {unmappedTypes.map((t) => (
                  <SelectItem key={t.id} value={String(t.id)}>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-[220px]">
            <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold mb-1">
              Pathway
            </div>
            <Select value={newPathway} onValueChange={setNewPathway}>
              <SelectTrigger data-testid="apm-add-pathway">
                <SelectValue placeholder="Pick a pathway" />
              </SelectTrigger>
              <SelectContent>
                {pathways.map((p) => (
                  <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => {
                const t = typeById.get(Number(newTypeId));
                upsertMapping({
                  appointment_type_id: Number(newTypeId),
                  appointment_type_name: t?.name || null,
                  pathway_key: newPathway,
                });
              }}
              disabled={busy || !newTypeId || !newPathway}
              size="sm"
              className="bg-zinc-950 hover:bg-zinc-800 text-white"
              data-testid="apm-add-save"
            >
              Add mapping
            </Button>
            <Button variant="outline" size="sm" onClick={() => setAdding(false)}>Cancel</Button>
          </div>
        </div>
      ) : (
        <Button
          variant="outline"
          size="sm"
          className="mt-3"
          onClick={() => setAdding(true)}
          disabled={busy}
          data-testid="apm-add-open"
        >
          <Plus className="h-4 w-4 mr-1.5" /> Add mapping
        </Button>
      )}
    </Card>
  );
}


function MappingRow({ row, pathways, typeName, onChange, onDelete, disabled }) {
  return (
    <div
      className="flex flex-wrap items-center gap-3 p-3 border border-zinc-200 rounded"
      data-testid={`apm-row-${row.id}`}
    >
      <div className="flex-1 min-w-[220px]">
        <div className="font-medium text-zinc-950">{typeName}</div>
        <div className="text-xs text-zinc-500 flex items-center gap-1.5 mt-0.5">
          <span>Type ID: {row.appointment_type_id ?? "—"}</span>
          {row.auto_seeded && (
            <Badge variant="outline" className="text-2xs" data-testid="apm-row-auto-seeded">
              <Wand2 className="h-3 w-3 mr-1" /> auto-seeded
            </Badge>
          )}
        </div>
      </div>
      <div className="min-w-[220px]">
        <Select value={row.pathway_key} onValueChange={onChange} disabled={disabled}>
          <SelectTrigger data-testid={`apm-row-pathway-${row.id}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {pathways.map((p) => (
              <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="text-xs text-zinc-500 min-w-[180px]">
        {(row.module_keys || []).join(", ")}
      </div>
      <Button
        variant="ghost"
        size="icon"
        onClick={onDelete}
        disabled={disabled}
        data-testid={`apm-row-delete-${row.id}`}
        aria-label="Delete mapping"
      >
        <Trash2 className="h-4 w-4 text-zinc-500" />
      </Button>
    </div>
  );
}
